/**
 * Settings handlers for MSW
 * Handles password change and MFA endpoints for demo mode
 */

import { http, HttpResponse } from 'msw'
import { applyDelay, createErrorResponse } from '../utils'
import { PASSWORD_MIN_LENGTH, passwordChecks } from '#shared/utils/password'

const VALID_DEMO_CODE = '123456'
const DEMO_SECRET = 'JBSWY3DPEHPK3PXP'

// Helper to get MFA status from localStorage
function getDemoMFAStatus(): boolean {
  if (typeof window !== 'undefined') {
    return localStorage.getItem('demo:mfaEnabled') === 'true'
  }
  return false
}

// Helper to set MFA status in localStorage
function setDemoMFAStatus(enabled: boolean): void {
  if (typeof window !== 'undefined') {
    if (enabled) {
      localStorage.setItem('demo:mfaEnabled', 'true')
    } else {
      localStorage.removeItem('demo:mfaEnabled')
    }
  }
}

// Helper to validate password strength
function validatePassword(password: string): string[] {
  const errors: string[] = []
  if (!passwordChecks.minLength(password)) {
    errors.push(`Password must be at least ${PASSWORD_MIN_LENGTH} characters`)
  }
  if (!passwordChecks.uppercase(password)) {
    errors.push('Password must contain at least one uppercase letter')
  }
  if (!passwordChecks.lowercase(password)) {
    errors.push('Password must contain at least one lowercase letter')
  }
  if (!passwordChecks.number(password)) {
    errors.push('Password must contain at least one number')
  }
  if (!passwordChecks.symbol(password)) {
    errors.push('Password must contain at least one special character')
  }
  return errors
}

// Generate demo recovery codes
function generateDemoRecoveryCodes(): string[] {
  const codes: string[] = []
  for (let i = 0; i < 10; i++) {
    const part1 = Math.random().toString(36).substring(2, 6).toUpperCase()
    const part2 = Math.random().toString(36).substring(2, 6).toUpperCase()
    const part3 = Math.random().toString(36).substring(2, 6).toUpperCase()
    codes.push(`${part1}-${part2}-${part3}`)
  }
  return codes
}

export const settingsHandlers = [
  /**
   * POST /api/auth/change-password
   * Change user password
   */
  http.post('/api/auth/change-password', async ({ request }) => {
    const url = new URL(request.url)
    const searchParams = url.searchParams

    await applyDelay({ searchParams, preset: 'normal' })

    // Check for error simulation
    const errorParam = searchParams.get('_error')
    if (errorParam === '401') {
      return createErrorResponse(401, {
        message: 'Current password is incorrect'
      })
    }
    if (errorParam) {
      return createErrorResponse(500, {
        message: 'Unable to change password'
      })
    }

    const body = await request.json() as { currentPassword?: string, newPassword?: string }
    const { currentPassword, newPassword } = body

    if (!currentPassword || !newPassword) {
      return createErrorResponse(400, {
        message: 'Current password and new password are required'
      })
    }

    // In demo mode, accept any current password
    // but still validate new password strength
    const passwordErrors = validatePassword(newPassword)
    if (passwordErrors.length > 0) {
      return createErrorResponse(400, {
        message: passwordErrors[0]
      })
    }

    if (currentPassword === newPassword) {
      return createErrorResponse(400, {
        message: 'New password must be different from current password'
      })
    }

    return HttpResponse.json({
      success: true,
      message: 'Password changed successfully'
    })
  }),

  /**
   * POST /api/auth/mfa/setup
   * Initialize MFA setup - returns secret and QR URL
   */
  http.post('/api/auth/mfa/setup', async ({ request }) => {
    const url = new URL(request.url)
    const searchParams = url.searchParams

    await applyDelay({ searchParams, preset: 'normal' })

    // Check for error simulation
    const errorParam = searchParams.get('_error')
    if (errorParam) {
      return createErrorResponse(500, {
        message: 'Unable to setup MFA'
      })
    }

    const email = 'demo@a55.tech'
    const issuer = 'A55 Merchant Platform'
    const qrCodeUrl = `otpauth://totp/${encodeURIComponent(issuer)}:${encodeURIComponent(email)}?secret=${DEMO_SECRET}&issuer=${encodeURIComponent(issuer)}`

    return HttpResponse.json({
      secretCode: DEMO_SECRET,
      qrCodeUrl
    })
  }),

  /**
   * POST /api/auth/mfa/verify
   * Verify MFA code and enable MFA
   */
  http.post('/api/auth/mfa/verify', async ({ request }) => {
    const url = new URL(request.url)
    const searchParams = url.searchParams

    await applyDelay({ searchParams, preset: 'normal' })

    // Check for error simulation
    const errorParam = searchParams.get('_error')
    if (errorParam === '400') {
      return createErrorResponse(400, {
        message: 'Invalid verification code'
      })
    }
    if (errorParam) {
      return createErrorResponse(500, {
        message: 'Unable to verify MFA code'
      })
    }

    const body = await request.json() as { code?: string }
    const { code } = body

    if (!code) {
      return createErrorResponse(400, {
        message: 'Verification code is required'
      })
    }

    // In demo mode, accept "123456" as valid code
    if (code !== VALID_DEMO_CODE) {
      return createErrorResponse(400, {
        message: 'Invalid verification code'
      })
    }

    // Enable MFA in demo state
    setDemoMFAStatus(true)

    return HttpResponse.json({
      success: true,
      recoveryCodes: generateDemoRecoveryCodes()
    })
  }),

  /**
   * POST /api/auth/mfa/disable
   * Disable MFA (requires code verification)
   */
  http.post('/api/auth/mfa/disable', async ({ request }) => {
    const url = new URL(request.url)
    const searchParams = url.searchParams

    await applyDelay({ searchParams, preset: 'normal' })

    // Check for error simulation
    const errorParam = searchParams.get('_error')
    if (errorParam === '400') {
      return createErrorResponse(400, {
        message: 'Invalid verification code'
      })
    }
    if (errorParam) {
      return createErrorResponse(500, {
        message: 'Unable to disable MFA'
      })
    }

    const body = await request.json() as { code?: string }
    const { code } = body

    if (!code) {
      return createErrorResponse(400, {
        message: 'Verification code is required'
      })
    }

    // In demo mode, accept "123456" as valid code
    if (code !== VALID_DEMO_CODE) {
      return createErrorResponse(400, {
        message: 'Invalid verification code'
      })
    }

    // Disable MFA in demo state
    setDemoMFAStatus(false)

    return HttpResponse.json({
      success: true,
      message: 'Two-factor authentication has been disabled'
    })
  }),

  /**
   * GET /api/auth/mfa/status
   * Get current MFA status
   */
  http.get('/api/auth/mfa/status', async ({ request }) => {
    const url = new URL(request.url)
    const searchParams = url.searchParams

    await applyDelay({ searchParams, preset: 'fast' })

    // Check for error simulation
    const errorParam = searchParams.get('_error')
    if (errorParam) {
      return createErrorResponse(500, {
        message: 'Unable to get MFA status'
      })
    }

    const enabled = getDemoMFAStatus()

    return HttpResponse.json({
      enabled,
      preferredMethod: enabled ? 'TOTP' : null
    })
  })
]
